import { type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useCanGoBack, useRouter } from '@tanstack/react-router'

import { ArrowRightStartOnRectangleIcon } from '@gto/icons-react/outline/arrow-right-start-on-rectangle'
import { ChevronLeftIcon } from '@gto/icons-react/outline/chevron-left'
import { Button } from '@gto/ui/button'
import { cn } from '@gto/ui/cn'
import { Paper } from '@gto/ui/paper'
import { Typography } from '@gto/ui/typography'

import type { AffiliateAdminPartnerDetailSchema } from '@/api/gen'
import { MutationError } from '@/components/mutation-error'
import { StatusBadge } from '@/components/status-badge'
import { TierTag } from '@/components/tier-tag'
import { countryFlagEmoji } from '@/utils/country-flag'
import { isTempEmail } from '@/utils/is-temp-email'

import { AccessSection } from './access-section'
import { getInitials } from './get-initials'
import { NotesSection } from './notes-section'
import { RailSection } from './rail-section'

type IdentityRailProps = {
  partner: AffiliateAdminPartnerDetailSchema
  workflowError: boolean
  /** Sanitised backend reason, e.g. "that coupon code is already taken". */
  workflowErrorDetail: string | null
  onApprove: () => void
  onActivate: () => void
  onReject: () => void
  onSuspend: () => void
  /**
   * Renders the "Log in as" button when provided. The caller (the partner
   * detail page) decides whether to pass a handler — admin-gated via
   * `useIsAdmin()` — so the rail does not need to know about role checks.
   */
  onImpersonate?: () => void
  /** Inline error by the "Log in as" button when starting impersonation fails. */
  impersonateError?: boolean
  /**
   * True when the admin reached this detail from the partners LIST. It lets the
   * "Back to partners" control step back in history to the exact filtered /
   * paged / sorted list they left; every other origin (ledger, dashboard,
   * orders, a deep link) gets a plain link to the bare list so the control
   * always honours its "Partners" label. See MKT-439.
   */
  cameFromPartnersList?: boolean
}

export const IdentityRail = ({
  partner,
  workflowError,
  workflowErrorDetail,
  onApprove,
  onActivate,
  onReject,
  onSuspend,
  onImpersonate,
  impersonateError = false,
  cameFromPartnersList = false,
}: IdentityRailProps) => {
  const intl = useIntl()
  const { formatMessage } = intl
  const router = useRouter()
  const canGoBack = useCanGoBack()
  // Step back to the list only when the admin came from it AND there is history
  // to pop; every other origin gets the plain link below so the control honours
  // its "Partners" label (MKT-439).
  const canReturnToList = cameFromPartnersList && canGoBack

  const memberSince = intl.formatDate(new Date(partner.created_at), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const approvedAt =
    partner.approved_at == null
      ? null
      : intl.formatDate(new Date(partner.approved_at), {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })

  const backLinkClassName =
    'inline-flex w-fit items-center gap-1 text-text-secondary no-underline hover:text-text-primary'
  const backLinkContent: ReactNode = (
    <>
      <ChevronLeftIcon
        height={14}
        width={14}
      />
      <Typography
        // tracking-[0.06em] matches the dashboard-kpi-tile eyebrow. No
        // letterSpacing token (the design tokens are all px-based).
        className="text-xs font-medium uppercase tracking-[0.06em]"
        color="inherit"
        variant="caption"
      >
        <FormattedMessage id="admin.partnerDetail.backToPartners" />
      </Typography>
    </>
  )

  return (
    <Paper
      // Full-viewport-height sticky inspector: the rail spends horizontal
      // space to keep identity/status/workflow/notes out of the vertical
      // budget, and the notes textarea grows to absorb the remaining height
      // (no dead column). The 80px in the calc is the _private layout's
      // vertical chrome (main p-1 + content p-4, top and bottom), so at rest
      // the rail ends exactly at the content box and adds no page scroll; no
      // token covers viewport-relative sizes. On md (≤1024) the page stacks
      // to one column, so the rail returns to content height.
      className="sticky top-2 flex h-[calc(100vh-80px)] min-h-[560px] flex-col gap-4 overflow-y-auto rounded-md p-5 md:static md:h-auto md:min-h-0"
      elevation="2"
    >
      {/* When the admin came from the partners list, the row-click pushed this
          detail on top of that filtered/paged/sorted list URL, so stepping back
          in history restores it verbatim (incl. scroll). Every other origin —
          ledger, dashboard, orders, a deep link — links to the bare list, which
          is what the "Partners" label promises; a blanket `history.back()`
          would send those flows back to where they came from instead (MKT-439). */}
      {canReturnToList ? (
        <button
          className={cn(backLinkClassName, 'cursor-pointer border-0 bg-transparent p-0')}
          data-tst="partner-detail-back"
          type="button"
          onClick={() => router.history.back()}
        >
          {backLinkContent}
        </button>
      ) : (
        <Link
          className={backLinkClassName}
          data-tst="partner-detail-back"
          to="/admin/partners"
        >
          {backLinkContent}
        </Link>
      )}

      <div className="flex items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-paper-elevation4 font-heading text-base font-semibold text-text-primary">
          {getInitials(partner.display_name)}
        </span>
        <div className="flex min-w-0 flex-col">
          <Typography
            className="font-heading font-semibold leading-120"
            variant="subtitle1"
          >
            {partner.display_name}
          </Typography>
          {isTempEmail(partner.email) ? (
            <Typography
              className="italic"
              color="text.secondary"
              variant="caption"
            >
              <FormattedMessage id="admin.partners.tempEmail.label" />
            </Typography>
          ) : (
            <Typography
              className="truncate"
              color="text.secondary"
              variant="caption"
            >
              {partner.email}
            </Typography>
          )}
        </div>
      </div>

      <div className="w-fit">
        <StatusBadge status={partner.status} />
      </div>

      <WorkflowAction
        status={partner.status}
        onActivate={onActivate}
        onApprove={onApprove}
        onReject={onReject}
        onSuspend={onSuspend}
      />

      <MutationError
        data-tst="workflow-error"
        detail={workflowErrorDetail}
        messageId="admin.partnerDetail.workflow.error"
        show={workflowError}
      />

      {onImpersonate !== undefined && (
        <Button
          fullWidth
          color="secondary"
          data-tst="partner-impersonate"
          variant="outlined"
          startIcon={
            <ArrowRightStartOnRectangleIcon
              height={16}
              width={16}
            />
          }
          onClick={onImpersonate}
        >
          <FormattedMessage id="admin.partnerDetail.impersonate" />
        </Button>
      )}

      <MutationError
        data-tst="impersonate-error"
        messageId="admin.partnerDetail.impersonate.error"
        show={impersonateError}
      />

      {/* contents at desktop (the Paper's flex gap spaces the sections);
          side-by-side grid on md, where the stacked rail must stay short. */}
      <div className="contents md:grid md:grid-cols-2 md:gap-4">
        <RailSection title={formatMessage({ id: 'admin.partnerDetail.about' })}>
          <RailRow
            label={formatMessage({ id: 'admin.partnerDetail.tier' })}
            value={
              partner.tier == null ? (
                <span className="text-text-disabled">
                  <FormattedMessage id="admin.partnerDetail.none" />
                </span>
              ) : (
                <TierTag
                  commissionRate={partner.tier.commission_rate}
                  tierName={partner.tier.name}
                />
              )
            }
          />
          <RailRow
            label={formatMessage({ id: 'profile.country' })}
            value={
              partner.country ? (
                `${countryFlagEmoji(partner.country)} ${partner.country.toUpperCase()}`
              ) : (
                <span className="text-text-disabled">
                  <FormattedMessage id="admin.partnerDetail.unknown" />
                </span>
              )
            }
          />
          <RailRow
            label={formatMessage({ id: 'admin.partnerDetail.memberSince' })}
            value={memberSince}
          />
          <RailRow
            label={formatMessage({ id: 'admin.partnerDetail.approved' })}
            value={
              approvedAt ?? (
                <span className="text-text-disabled">
                  <FormattedMessage id="admin.partnerDetail.notApproved" />
                </span>
              )
            }
          />
        </RailSection>

        <AccessSection
          partner={partner}
          partnerId={partner.id}
        />
      </div>

      <NotesSection
        notes={partner.notes}
        partnerId={partner.id}
      />
    </Paper>
  )
}

type WorkflowActionProps = {
  status: string
  onApprove: () => void
  onActivate: () => void
  onReject: () => void
  onSuspend: () => void
}

const WorkflowAction = ({
  status,
  onApprove,
  onActivate,
  onReject,
  onSuspend,
}: WorkflowActionProps) => {
  if (status === 'PENDING_APPROVAL') {
    return (
      <div className="flex flex-col gap-2 rounded-md border border-warning-main/25 bg-paper-elevation2 p-3">
        <Typography variant="body2">
          <FormattedMessage id="admin.partnerDetail.workflow.pendingPrompt" />
        </Typography>
        <div className="flex gap-2">
          <Button
            className="flex-1"
            color="primary"
            data-tst="workflow-approve"
            size="small"
            variant="contained"
            onClick={onApprove}
          >
            <FormattedMessage id="admin.partnerDetail.approve" />
          </Button>
          <Button
            className="flex-1"
            color="secondary"
            data-tst="workflow-reject"
            size="small"
            variant="outlined"
            onClick={onReject}
          >
            <FormattedMessage id="admin.partnerDetail.reject" />
          </Button>
        </div>
      </div>
    )
  }

  if (status === 'APPROVED') {
    return (
      <div className="flex flex-col gap-2 rounded-md border border-info-main/25 bg-paper-elevation2 p-3">
        <Typography variant="body2">
          <FormattedMessage id="admin.partnerDetail.workflow.approvedPrompt" />
        </Typography>
        <div className="flex gap-2">
          <Button
            className="flex-1"
            color="primary"
            data-tst="workflow-activate"
            size="small"
            variant="contained"
            onClick={onActivate}
          >
            <FormattedMessage id="admin.partnerDetail.activate" />
          </Button>
          <Button
            className="flex-1"
            color="secondary"
            data-tst="workflow-reject"
            size="small"
            variant="outlined"
            onClick={onReject}
          >
            <FormattedMessage id="admin.partnerDetail.reject" />
          </Button>
        </div>
      </div>
    )
  }

  if (status === 'SUSPENDED') {
    return (
      <Button
        fullWidth
        color="primary"
        data-tst="workflow-reactivate"
        variant="contained"
        onClick={onApprove}
      >
        <FormattedMessage id="admin.partnerDetail.workflow.reactivate" />
      </Button>
    )
  }

  if (status === 'ACTIVE') {
    return (
      <Button
        fullWidth
        color="error"
        data-tst="workflow-suspend"
        variant="outlined"
        onClick={onSuspend}
      >
        <FormattedMessage id="admin.partnerDetail.suspend" />
      </Button>
    )
  }

  return null
}

type RailRowProps = {
  label: string
  value: ReactNode
  mono?: boolean
}

const RailRow = ({ label, value, mono = false }: RailRowProps) => (
  <div className="flex items-baseline justify-between gap-3">
    <Typography
      className="shrink-0"
      color="text.secondary"
      variant="caption"
    >
      {label}
    </Typography>
    <Typography
      className={cn('min-w-0 text-right', mono && 'font-mono')}
      variant="caption"
    >
      {value}
    </Typography>
  </div>
)
